import { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const sortParam = searchParams.get('sort');
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  const filteredComics = useMemo(() => {
    let result = activeSlug === 'all'
      ? [...comics]
      : comics.filter((c) => c.category === activeSlug);

    if (sortParam === 'ranking') {
      result.sort((a, b) => {
        const parseViews = (v: string) => parseInt(v.replace('万', '0000'));
        return parseViews(b.views) - parseViews(a.views);
      });
    } else {
      result.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime());
    }

    return result;
  }, [activeSlug, sortParam]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const pageTitle = activeCategory
    ? `${activeCategory.name}漫画 - 韩国漫画在线阅读`
    : '全部漫画 - 韩国漫画在线阅读';

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: activeCategory ? `${activeCategory.name}漫画` : '全部漫画' },
            ]}
          />

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": pageTitle,
            "description": `韩漫网站${activeCategory ? activeCategory.name : '全部分类'}韩国漫画在线阅读，最新免费漫画大全`,
            "url": `https://www.hanmanwang.com/category/${activeSlug}`,
            "inLanguage": "zh-CN",
            "isPartOf": {
              "@type": "WebSite",
              "name": "韩漫网",
              "url": "https://www.hanmanwang.com/"
            }
          }) }} />

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "首页",
                "item": "https://www.hanmanwang.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": activeCategory ? `${activeCategory.name}漫画` : "全部漫画"
              }
            ]
          }) }} />

          <div className="px-4 md:px-6 py-6">
            <h1 className="text-2xl font-heading text-foreground-900 mb-6 whitespace-nowrap">
              <strong>{activeCategory ? activeCategory.name : '全部'}漫画</strong>
              <span className="text-sm font-normal text-foreground-500 ml-3">
                共 {filteredComics.length} 部
              </span>
            </h1>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => {
                const href = cat.slug === 'all'
                  ? '/category/all'
                  : `/category/${cat.slug}`;
                const isActive = cat.slug === activeSlug;
                return (
                  <a
                    key={cat.slug}
                    href={href}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveSlug(cat.slug);
                      window.history.pushState({}, '', href);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      isActive
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </a>
                );
              })}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的漫画</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}